import React from "react";
import { useNavigate } from "react-router-dom";
import "./PolicyPages.css";

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div>
      <div className="auth-header">
        <div className="brand" onClick={() => navigate("/")}>
          <img
            src="/images/customroam-logo.png"
            alt="Custom Roam"
            className="logo-img"
          />
          <span className="brand-text">CUSTOM ROAM</span>
        </div>

        <div className="back-home" onClick={() => navigate("/")}>
          Back to Home
        </div>
      </div>

      <div className="policy-container">
        <h1>Custom Roam Privacy Policy</h1>

        <p>
          <strong>Effective Date:</strong> July 11, 2026
        </p>

        <p>
          <strong>Last Updated:</strong> July 11, 2026
        </p>

        <hr />

        <h2>1. Introduction</h2>

        <p>
          Custom Roam ("Custom Roam," "we," "us," or "our") respects your
          privacy and is committed to protecting your personal information.
        </p>

        <p>
          This Privacy Policy explains how we collect, use, store, disclose,
          and protect information when you access or use:
        </p>

        <ul>
          <li>The Custom Roam website</li>
          <li>The Custom Roam mobile applications</li>
          <li>Roam Share</li>
          <li>Itinerary planning tools</li>
          <li>User profiles</li>
          <li>Notifications</li>
          <li>Bucket Lists</li>
          <li>Shared itineraries</li>
          <li>
            Any related products, features, or services collectively referred
            to as the "Services"
          </li>
        </ul>

        <p>
          By accessing or using Custom Roam, you acknowledge that you have read
          and understood this Privacy Policy.
        </p>

        <hr />

        <h2>2. Information We Collect</h2>

        <h3>A. Information You Provide</h3>

        <p>
          We may collect information that you voluntarily provide, including:
        </p>

        <ul>
          <li>Full name</li>
          <li>Email address</li>
          <li>Password, stored in hashed form</li>
          <li>Date of birth</li>
          <li>Gender</li>
          <li>Phone number, if provided</li>
          <li>Username</li>
          <li>Profile photo</li>
          <li>Biography or profile description</li>
          <li>Travel preferences</li>
          <li>Trip-planning preferences</li>
          <li>Saved itineraries</li>
          <li>Shared itineraries</li>
          <li>Bucket Lists and Bucket List items</li>
          <li>
            Verification codes, such as one-time passwords used for account
            verification
          </li>
          <li>Photos and videos uploaded to Roam Share</li>
          <li>Comments and replies</li>
          <li>Likes, reactions, and other interactions</li>
          <li>
            Reports submitted regarding content or users, including the reason
            for the report and related moderation information
          </li>
          <li>Customer-support inquiries and communications</li>
          <li>Following and follower relationships</li>
          <li>Notification history and notification preferences</li>
        </ul>

        <h3>B. Information Collected Automatically</h3>

        <p>
          When you use Custom Roam, certain technical and location-related
          information may be collected automatically, including:
        </p>

        <ul>
          <li>IP address</li>
          <li>Device type</li>
          <li>Operating system</li>
          <li>App version</li>
          <li>Date and time of access</li>
          <li>Authentication and session information</li>
          <li>
            Approximate location information, such as country, state, province,
            or region
          </li>
          <li>
            Log information necessary for security, troubleshooting, and
            platform operation
          </li>
          <li>Login activity and account-access timestamps</li>
        </ul>

        <p>
          Custom Roam does not collect precise GPS coordinates or background
          location information.
        </p>

        <h3>C. Push Notification Information</h3>

        <p>If you enable notifications, we may collect:</p>

        <ul>
          <li>Push notification tokens</li>
          <li>Device notification settings</li>
        </ul>

        <p>
          This information is used solely for delivering notifications and
          improving communication.
        </p>

        <hr />

        <h2>3. How We Use Information</h2>

        <p>We use information for purposes including:</p>

        <ul>
          <li>Creating and managing user accounts</li>
          <li>Authenticating users</li>
          <li>Generating travel itineraries</li>
          <li>Saving and sharing itineraries</li>
          <li>Operating Roam Share</li>
          <li>Displaying public profiles</li>
          <li>Managing followers and social interactions</li>
          <li>Providing customer support</li>
          <li>Delivering notifications</li>
          <li>Personalizing content and recommendations</li>
          <li>Improving platform performance</li>
          <li>Monitoring engagement and usage</li>
          <li>Detecting fraud, abuse, spam, and security threats</li>
          <li>Moderating content and investigating reports</li>
          <li>Complying with legal obligations</li>
          <li>Protecting users and the platform</li>
        </ul>

        <hr />

        <h2>4. Legal Basis for Processing for EEA and UK Users</h2>

        <p>
          Where applicable under the GDPR and similar laws, we process personal
          information based on:
        </p>

        <ul>
          <li>Your consent</li>
          <li>Performance of a contract</li>
          <li>Compliance with legal obligations</li>
          <li>Legitimate business interests</li>
          <li>Protection of vital interests</li>
          <li>Public-interest obligations where required by law</li>
        </ul>

        <hr />

        <h2>5. Public Profiles and User Content</h2>

        <p>
          Certain information may be visible to other users and, in some cases,
          the public. This may include:
        </p>

        <ul>
          <li>Username</li>
          <li>Profile photo</li>
          <li>Biography or profile description</li>
          <li>Roam Share posts</li>
          <li>Photos and videos</li>
          <li>Comments and replies</li>
          <li>Shared itineraries designated as public</li>
          <li>Follower and following relationships</li>
        </ul>

        <p>
          Information that you choose to make public may be viewed, copied,
          shared, indexed, or redistributed by other users or third parties.
        </p>

        <p>
          Custom Roam cannot guarantee the removal of copies made by third
          parties after content becomes public.
        </p>

        <hr />

        <h2>6. How We Share Information</h2>

        <p>
          <strong>We do not sell your personal information.</strong>
        </p>

        <p>
          We may share information with trusted vendors and service providers
          that help us operate, maintain, secure, and improve the platform,
          including:
        </p>

        <ul>
          <li>Hosting and cloud infrastructure providers</li>
          <li>Database and storage providers</li>
          <li>Email and communication providers</li>
          <li>Push notification and messaging providers</li>
          <li>Mapping and location service providers</li>
          <li>Security and fraud-prevention providers</li>
          <li>Customer-support providers</li>
        </ul>

        <p>
          These providers are given access only to information reasonably
          necessary to perform services on our behalf and are expected to
          protect that information in accordance with applicable laws and
          contractual obligations.
        </p>

        <hr />

        <h2>7. No Sale of Personal Information</h2>

        <p>
          Custom Roam does not sell users' personal information to third
          parties.
        </p>

        <hr />

        <h2>8. Changes in Ownership</h2>

        <p>
          If ownership of Custom Roam changes through a merger, acquisition,
          investment, restructuring, sale of assets, financing transaction, or
          another corporate transaction, user information may be transferred as
          part of that transaction, subject to applicable law.
        </p>

        <hr />

        <h2>9. International Users and Data Transfers</h2>

        <p>
          Custom Roam is operated internationally and may be accessed by users
          worldwide.
        </p>

        <p>
          By using the Services, you understand that your information may be
          transferred to, stored in, and processed in countries outside your
          country of residence, including countries that may have different
          data-protection laws than those in your jurisdiction.
        </p>

        <p>
          We take reasonable measures to protect personal information
          regardless of where it is processed and implement safeguards where
          required by applicable law.
        </p>

        <hr />

        <h2>10. Cookies, Local Storage, and Similar Technologies</h2>

        <p>
          We use cookies, local storage, and similar technologies to:
        </p>

        <ul>
          <li>Keep users logged in</li>
          <li>Remember preferences</li>
          <li>Improve the user experience</li>
          <li>Analyze platform usage</li>
          <li>Enhance security</li>
          <li>Measure performance</li>
        </ul>

        <p>
          Disabling cookies or browser storage may affect certain platform
          functionality.
        </p>

        <hr />

        <h2>11. Data Security</h2>

        <p>
          We implement reasonable technical, administrative, and organizational
          safeguards to protect information, including:
        </p>

        <ul>
          <li>HTTPS encryption</li>
          <li>Password hashing</li>
          <li>Token-based authentication</li>
          <li>Access controls</li>
          <li>Security monitoring</li>
          <li>Input validation and sanitization</li>
        </ul>

        <p>
          While we take reasonable precautions, no method of transmission or
          storage can be guaranteed to be completely secure.
        </p>

        <hr />

        <h2>12. Data Retention</h2>

        <p>
          We retain personal information for as long as reasonably necessary
          to:
        </p>

        <ul>
          <li>Provide our Services</li>
          <li>Maintain accounts</li>
          <li>Resolve disputes</li>
          <li>Enforce agreements</li>
          <li>Comply with legal obligations</li>
          <li>Protect platform security</li>
          <li>Prevent fraud and abuse</li>
        </ul>

        <p>
          Retention periods may vary depending on the nature of the information
          and applicable legal requirements.
        </p>

        <p>
          Verification codes and temporary signup records may be automatically
          deleted after expiration. Push notification tokens may be removed
          when they are no longer valid or when users disable notifications.
        </p>

        <hr />

        <h2>13. Account Deletion</h2>

        <p>
          Users may permanently delete their Custom Roam account at any time
          through the Account Settings available within the Services.
        </p>

        <p>
          When an account is deleted, we will delete or anonymize your personal
          information and remove associated user-generated content in
          accordance with this Privacy Policy, except where we are required or
          permitted by law to retain certain information for legal, security,
          fraud-prevention, dispute-resolution, regulatory-compliance,
          auditing, or other legitimate business purposes.
        </p>

        <p>
          Media files associated with deleted content are also removed from our
          storage systems as part of the deletion process, subject to backup
          and disaster-recovery processes.
        </p>

        <p>
          Account deletion is permanent and cannot be reversed once completed.
        </p>

        <hr />

        <h2>14. Content Moderation and User Reports</h2>

        <p>
          Users may report posts, comments, users, or other content that they
          believe violates our Terms and Conditions, applicable laws, or
          community standards.
        </p>

        <p>
          When a report is submitted, we may collect information relating to
          the report, including the reporting user, the reported content or
          account, the reason for the report, and the date and time of
          submission.
        </p>

        <p>
          We use this information to investigate reports, enforce our Terms and
          Conditions, protect users, prevent abuse, improve platform safety,
          comply with legal obligations, and maintain the integrity of the
          Services.
        </p>

        <p>
          We may review, restrict, remove, disable, or otherwise moderate
          content and accounts where appropriate.
        </p>

        <hr />

        <h2>15. Your Privacy Rights</h2>

        <p>
          Depending on your location, you may have rights to:
        </p>

        <ul>
          <li>Access personal information</li>
          <li>Correct inaccurate information</li>
          <li>Delete personal information</li>
          <li>Restrict processing</li>
          <li>Object to processing</li>
          <li>Withdraw consent</li>
          <li>Request data portability</li>
        </ul>

        <p>
          We may request identity verification before fulfilling a request.
        </p>

        <p>
          Privacy-related requests may be submitted to{" "}
          <a href="mailto:support@customroam.com">
            support@customroam.com
          </a>
          .
        </p>

        <hr />

        <h2>16. Children's Privacy</h2>

        <p>
          Custom Roam is intended for individuals who are at least 13 years
          old, or the minimum legal age required in their jurisdiction.
        </p>

        <p>
          We do not knowingly collect personal information from children under
          13.
        </p>

        <p>
          If we become aware that information has been collected from a child
          under 13 without appropriate authorization, we will take reasonable
          steps to remove that information.
        </p>

        <hr />

        <h2>17. Third-Party Services and Links</h2>

        <p>
          Custom Roam may integrate with or link to third-party services,
          including:
        </p>

        <ul>
          <li>Google Maps Platform and Google Places API</li>
          <li>Google Workspace for email delivery</li>
          <li>Expo</li>
          <li>Firebase Cloud Messaging</li>
          <li>Railway for application hosting</li>
          <li>Neon PostgreSQL for database hosting</li>
          <li>AWS S3 and related content-delivery services for media storage</li>
        </ul>

        <p>
          These services operate under their own privacy policies and terms.
          Custom Roam is not responsible for the privacy practices, content, or
          policies of third-party services.
        </p>

        <p>
          We encourage users to review the privacy policies of third-party
          services they choose to access.
        </p>

        <hr />

        <h2>18. Legal Requirements</h2>

        <p>We may disclose information when required to:</p>

        <ul>
          <li>
            Comply with applicable laws, regulations, legal processes, or
            governmental requests
          </li>
          <li>Enforce our Terms and Conditions</li>
          <li>
            Protect the rights, property, safety, or security of Custom Roam,
            our users, or others
          </li>
          <li>
            Detect, investigate, or prevent fraud, abuse, security incidents,
            or illegal activity
          </li>
        </ul>

        <hr />

        <h2>19. Changes to This Privacy Policy</h2>

        <p>
          We may modify this Privacy Policy from time to time.
        </p>

        <p>
          Changes may be posted on our website or mobile applications or
          communicated through email, notifications, or other reasonable
          means.
        </p>

        <p>
          Your continued use of the Services after changes become effective
          constitutes acceptance of the updated Privacy Policy.
        </p>

        <hr />

        <h2>20. Contact Us</h2>

        <p>
          For privacy questions, concerns, or requests, contact us at:
        </p>

        <p>
          <strong>Email:</strong>{" "}
          <a href="mailto:support@customroam.com">
            support@customroam.com
          </a>
        </p>

        <p>
          <strong>Website:</strong>{" "}
          <a
            href="https://www.customroam.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.customroam.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;